import React, { forwardRef } from 'react';
import { Trans } from 'react-i18next';
import type { View } from 'react-native';

import { Box, Button, HStack, Text, VStack, theme } from '@squire/ui';

import { ArrowDownFilledIcon } from '@/components/_icons/ArrowFownFilledIcon';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  DateFormat,
  formatDate,
  parseDateWithTimezone,
} from '@/utils/dateUtils';

import { styles } from './styles';
import type { CancellationPolicyConsentProps } from './types';

const CancellationPolicyConsent = forwardRef<
  View,
  CancellationPolicyConsentProps
>(({ canCancelBefore, testID, onAgree, shopTimezone, ...props }, ref) => {
  const { t } = useScreenTranslations('CancellationPolicyConsent');

  const cancellationDate = parseDateWithTimezone(
    canCancelBefore || '',
    shopTimezone || 'America/New_York',
  );

  const cancelHours = formatDate(cancellationDate, DateFormat.TIME);
  const cancelDate = formatDate(
    cancellationDate,
    DateFormat.DAY_MONTH_FRIENDLY,
  );

  return (
    <VStack {...props} flex ref={ref} testID={testID}>
      <Text variant="2xlStrong">{t('title')}</Text>

      <VStack
        flex
        centerHorizontal
        centerVertical
        space={theme.spacing[7]}
        paddingHorizontal={theme.spacing[5]}
      >
        <VStack style={styles.timelineChart}>
          <VStack centerVertical style={styles.animatedContainer}>
            <VStack style={styles.timelineCard}>
              <Text
                numberOfLines={1}
                variant="lStrong"
                color="textPrimaryInverse"
              >
                {cancelHours}
              </Text>
              <Text numberOfLines={1} variant="m" color="textSecondaryInverse">
                {cancelDate}
              </Text>
            </VStack>

            <Box style={styles.timelineChartArrow}>
              <ArrowDownFilledIcon size={12} color="iconPrimary" />
            </Box>
          </VStack>

          <Box style={styles.dateTimeline}>
            <Box style={styles.dateTimelineProgress} />
          </Box>
          <HStack style={styles.timelineLabels} space={theme.spacing[2]}>
            <Text color="textSecondaryInverse" variant="m" align="center">
              {t('now')}
            </Text>
            <Text color="textSecondaryInverse" variant="m" align="center">
              {formatDate(
                cancellationDate,
                DateFormat.DAY_MONTH_FRIENDLY_SHORT,
              ).toUpperCase()}
            </Text>
          </HStack>
        </VStack>

        <Text color="textPrimary" variant="l" align="center">
          <Trans
            i18nKey="CancellationPolicyConsent.description"
            values={{
              time: cancelHours,
              date: cancelDate,
            }}
            components={{
              strong: <Text variant="lStrong" children="" />,
            }}
          />
        </Text>
      </VStack>

      <VStack style={styles.buttonContainer}>
        <Button variant="primary" onPress={onAgree}>
          {t('understand')}
        </Button>
      </VStack>
    </VStack>
  );
});

CancellationPolicyConsent.displayName = 'CancellationPolicyConsent';

export { CancellationPolicyConsent };
